import { useEffect, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import useFlow from '@/hooks/use-flow.ts';
import useSocket from '@/hooks/use-sockets';
import useFlowStore from '@/store/flow';
import type {
	TCanvas,
	TCanvasFrontend,
	TCommand,
	TGroupItem,
	TGroupItemFrontend,
	TLibrary,
	TLibraryFrontend
} from '@/types/common';
import { SocketEvents } from '@/types/global-events.types.ts';
import { getArgumentsDictionary } from '@/utils';
import { markdownToEditorState } from '@/utils/editor-conversion';
import { safeParse } from '@/utils/safe-parse';

type HandlerFn = (args: Record<string, string>) => void;

const toFrontendGroupItem = (item: TGroupItem): TGroupItemFrontend => ({
	...item,
	title: markdownToEditorState(item.title ?? ''),
	body: markdownToEditorState(item.body ?? '')
});

const toFrontendCanvas = (canvas: TCanvas): TCanvasFrontend => ({
	...canvas,
	types: canvas.types.map((type) => ({
		...type,
		items: type.items.map(toFrontendGroupItem)
	}))
});

const toFrontendLibrary = (library: TLibrary): TLibraryFrontend =>
	library.map((group) => ({
		...group,
		items: group.items.map((item) => ({
			...item,
			title: markdownToEditorState(item.title ?? ''),
			body: markdownToEditorState(item.body ?? '')
		}))
	}));

export function useSocketEvents() {
	const { subscribe, isConnected, connectionKey } = useSocket();

	const isCanvasInitialized = useRef(false);
	const isLibraryInitialized = useRef(false);
	const activeConnectionKey = useRef<string | null>(null);

	const { setCanvas, setLibrary, updateItem } = useFlowStore(
		useShallow((state) => ({
			setCanvas: state.setCanvas,
			setLibrary: state.setLibrary,
			updateItem: state.updateItem
		}))
	);

	const { initCanvas, initLibrary } = useFlow();

	useEffect(() => {
		if (activeConnectionKey.current === connectionKey) {
			return;
		}

		activeConnectionKey.current = connectionKey;
		isCanvasInitialized.current = false;
		isLibraryInitialized.current = false;
	}, [connectionKey]);

	useEffect(() => {
		if (!isConnected) {
			return;
		}

		// handlers
		const handlers: Record<SocketEvents, HandlerFn> = {
			[SocketEvents.UPDATE_CANVAS]: (args) => {
				const canvas = safeParse<TCanvas>(args.body, { types: [], title: '' });
				setCanvas(toFrontendCanvas(canvas));
				isCanvasInitialized.current = true;
			},
			[SocketEvents.UPDATE_LIBRARY]: (args) => {
				const body = safeParse<TLibrary>(args.body, []);
				setLibrary(toFrontendLibrary(body));
				isLibraryInitialized.current = true;
			},
			[SocketEvents.UPDATE_ITEM]: (args) => {
				const item = <TGroupItem>JSON.parse(args.body);

				if (item.idn) {
					updateItem(args.type_idn, toFrontendGroupItem(item));
				}
			}
		};

		// subscribe once
		const unsubscribe = subscribe<TCommand>('command', (data) => {
			const handler = handlers[data.command_idn as SocketEvents];
			if (!handler) {
				return;
			}

			try {
				const args = getArgumentsDictionary(data.arguments);
				handler(args);
			} catch (e) {
				console.error('Socket handler failed for', data.command_idn, e);
			}
		});

		if (!isCanvasInitialized.current) {
			initCanvas();
		}
		if (!isLibraryInitialized.current) {
			initLibrary();
		}

		return () => unsubscribe();
	}, [
		isConnected,
		subscribe,
		initCanvas,
		initLibrary,
		setCanvas,
		setLibrary,
		updateItem
	]);
}
